import { createNoopLogger } from '@moojo/logger'
import { errorLike, shouldNeverHappen } from '@moojo/misc-constructs'
import { MoojoAgentServiceDeclaration } from '@moojo/moojo-agent-api'
import { ServiceClient } from '@moojo/serving-box-client'
import { langConfigForRawLang } from '@moojo/shared-config'
import { createRestTransfer } from '@moojo/web-transfer'

import type { EventTracker } from '../analytics/event-tracker'
import { extensionMarkup } from './extension-markup'

export const CouponExtension = {
  name: 'CouponContainer',
}

export const makeCouponExtension = (eventTracker: EventTracker) => ({
  name: CouponExtension.name,
  type: 'response',
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  match: ({ trace }: { trace: any }) => trace.type === 'coupon' || trace.payload?.name === 'coupon',
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  render: async ({ trace, element }: { trace: any; element: Element }) => {
    const data = trace?.payload?.data
    const { apiBase, email, couponTag, language, showUrlLink } = data ?? {}

    try {
      const transfer = createRestTransfer(apiBase)
      const client = new ServiceClient(createNoopLogger(), MoojoAgentServiceDeclaration, transfer, 'envelope')

      const result = await client.callEndpoint('moojo-agent/generate-coupon-link', {
        email,
        language,
        tag: couponTag,
      })

      // TODO(roman): it might be a good idea to return the effective normalized language from the endpoint

      if (result.status !== 'generated') {
        throw new Error(`Unexpected coupon link generation result: ${result.status}`)
      }

      const { kind, url } = result.link

      if (kind === 'image') {
        const container = document.createElement('div')
        container.classList.add('coupon-container')
        container.style.display = 'flex'
        container.style.flexDirection = 'column'
        container.style.alignItems = 'center'

        const img = document.createElement('img')
        img.src = url
        img.style.width = getOffsetWidth(element.parentElement)
        img.style.height = 'auto'
        container.appendChild(img)

        if (showUrlLink === 'true') {
          const link = document.createElement('a')
          link.classList.add('vfrc-message')
          link.href = url
          link.textContent = langConfigForRawLang(language).viewCouponLinkText // reminder: language cannot be undefined here, as it's validated in the endpoint
          link.style.marginTop = '10px'
          link.style.textAlign = 'center'
          link.style.fontSize = '80%'
          link.target = '_blank'
          link.addEventListener('click', e => {
            e.preventDefault()
            eventTracker.track('coupon_clicked', {
              couponTag,
              email,
              language,
              kind,
              url,
            })
            window.open(url, '_blank')
          })

          container.appendChild(link)
        }

        element.appendChild(container)
      } else if (kind === 'html') {
        const res = await fetch(url)
        if (res.status !== 200) {
          throw new Error(`Failed to fetch coupon html: ${res.status}`)
        }

        const html = await res.text()
        const dom = new DOMParser().parseFromString(html, 'text/html')

        const couponContainer = document.createElement('div')
        couponContainer.classList.add('coupon-container')

        // Move all head elements
        Array.from(dom.head.children).forEach(child => {
          couponContainer.appendChild(child)
        })

        // Move all body elements
        Array.from(dom.body.children).forEach(child => {
          couponContainer.appendChild(child)
        })

        element.appendChild(couponContainer)
      } else {
        shouldNeverHappen(kind)
        // TODO(roman): throw?
      }

      eventTracker.track('coupon_viewed', {
        couponTag,
        email,
        language,
        kind,
        url,
      })
    } catch (e) {
      element.innerHTML = extensionMarkup.error
      eventTracker.track('coupon_view_error', {
        couponTag,
        email,
        language,
        error: errorLike(e).message ?? 'unknown error',
      })
    }
  },
})

/**
 * Computing image width is tricky.
 * We need to find the closest parent with class vfrc-system-response
 * and use its offsetWidth.
 *
 * This is because the image is inside a vfrc-system-response div
 * and we need to make sure the image is not wider than the parent.
 */
const getOffsetWidth = (element: HTMLElement | null) => {
  let attempts = 3
  let currentElement: HTMLElement | null = element
  while (attempts-- >= 0 && currentElement) {
    if (currentElement.parentElement?.classList.contains('vfrc-system-response')) {
      const offsetWidth = currentElement?.offsetWidth
      if (offsetWidth) {
        return offsetWidth * 0.9 + 'px'
      }
    }

    currentElement = currentElement.parentElement
  }

  throw new Error('Failed to get offset width')
}
